import React, { useCallback, useEffect, useRef, useState } from 'react'

import './DashboardSharingControls.css'

import DashboardInviteLinksPanel from './DashboardInviteLinksPanel'
import DashboardCollaboratorsPanel from './DashboardCollaboratorsPanel'

import {
  disableDashboardSharing,
  enableDashboardSharing,
  getDashboardSharing,
  regenerateDashboardSharing,
  getShareLinks,
  createShareLink,
  revokeShareLink,
  deleteShareLink,
  getCollaborators,
  updateCollaboratorRole,
  removeCollaborator,
  removeAllCollaborators,
} from '../services/dashboardSharingApi'

const defaultApi = {
  get: getDashboardSharing,
  enable: enableDashboardSharing,
  regenerate: regenerateDashboardSharing,
  disable: disableDashboardSharing,
  listLinks: getShareLinks,
  createLink: createShareLink,
  revokeLink: revokeShareLink,
  deleteLink: deleteShareLink,
  listCollaborators: getCollaborators,
  patchCollaboratorRole: updateCollaboratorRole,
  deleteCollaborator: removeCollaborator,
  deleteAllCollaborators: removeAllCollaborators,
}

const messages = {
  enable: ['Sharing enabled.', 'Unable to enable sharing.'],
  regenerate: ['Sharing link regenerated.', 'Unable to regenerate sharing link.'],
  disable: ['Sharing disabled.', 'Unable to disable sharing.'],
  copy: ['Public link copied.', 'Unable to copy the public link.'],
}

const feedbackDuration = {
  success: 2500,
  error: 4000,
}

export const publicDashboardLink = (token) => (
  `${window.location.origin}${window.location.pathname}#/public/dashboard/${token}`
)

function DashboardSharingControls({ code, access, api = defaultApi }) {
  const [open, setOpen] = useState(false)

  // ------------------------- Public (anonymous) link state -------------------------
  const [sharing, setSharing] = useState(null)
  const [busy, setBusy] = useState('')
  const [feedback, setFeedback] = useState(null)
  const feedbackTimer = useRef(null)

  // ------------------------- Invite links state -------------------------
  const [links, setLinks] = useState([])
  const [linksError, setLinksError] = useState(null)
  const [justCreatedId, setJustCreatedId] = useState(null)

  // ------------------------- Collaborators state -------------------------
  const [collaborators, setCollaborators] = useState([])
  const [collaboratorsError, setCollaboratorsError] = useState(null)

  const clearFeedbackTimer = useCallback(() => {
    if (feedbackTimer.current) {
      clearTimeout(feedbackTimer.current)
      feedbackTimer.current = null
    }
  }, [])

  const showFeedback = useCallback((kind, message) => {
    clearFeedbackTimer()
    setFeedback({ kind, message })
    feedbackTimer.current = setTimeout(() => {
      setFeedback(null)
      feedbackTimer.current = null
    }, feedbackDuration[kind])
  }, [clearFeedbackTimer])

  useEffect(() => () => clearFeedbackTimer(), [clearFeedbackTimer])

  // Everything is fetched once, when the panel opens — never polled. Every
  // endpoint here is throttled server-side, and re-fetching after each
  // mutation (below) is enough to keep the panel accurate.
  useEffect(() => {
    if (!open) return undefined
    let active = true
    setBusy('load')
    setJustCreatedId(null)
    clearFeedbackTimer()
    setFeedback(null)
    setLinksError(null)
    setCollaboratorsError(null)

    api.get(code, access)
      .then(next => { if (active) setSharing(next) })
      .catch(() => { if (active) showFeedback('error', 'Unable to load sharing settings.') })
      .finally(() => { if (active) setBusy('') })

    api.listLinks(code, access)
      .then(next => { if (active) setLinks(next) })
      .catch(() => { if (active) setLinksError('Unable to load invite links.') })

    api.listCollaborators(code, access)
      .then(next => { if (active) setCollaborators(next) })
      .catch(() => { if (active) setCollaboratorsError('Unable to load people with access.') })

    return () => { active = false }
  }, [access, api, clearFeedbackTimer, code, open, showFeedback])

  const perform = async (action) => {
    if (busy || !sharing) return
    const [success, failure] = messages[action]
    setBusy(action)
    clearFeedbackTimer()
    setFeedback(null)
    try {
      const next = action === 'copy'
        ? await navigator.clipboard.writeText(publicDashboardLink(sharing.public_token)).then(() => sharing)
        : await api[action](code, access)
      setSharing(next)
      showFeedback('success', success)
    } catch {
      showFeedback('error', failure)
    } finally {
      setBusy('')
    }
  }

  const createLinkHandler = async (body) => {
    setBusy('create-link')
    setLinksError(null)
    try {
      const created = await api.createLink(code, access, body)
      setLinks(current => [created, ...current])
      setJustCreatedId(created.id)
      showFeedback('success', 'Invite link created.')
    } catch (err) {
      const detail = err.response?.data?.error
      setLinksError(detail || 'Unable to create the invite link.')
    } finally {
      setBusy('')
    }
  }

  const revokeLinkHandler = async (linkId) => {
    setBusy('revoke-link')
    setLinksError(null)
    try {
      await api.revokeLink(code, access, linkId)
      setLinks(current => current.filter(link => link.id !== linkId))
      showFeedback('success', 'Invite link revoked.')
    } catch {
      setLinksError('Unable to revoke that link.')
    } finally {
      setBusy('')
    }
  }

  const deleteLinkHandler = async (linkId) => {
    setBusy('delete-link')
    setLinksError(null)
    try {
      await api.deleteLink(code, access, linkId)
      setLinks(current => current.filter(link => link.id !== linkId))
      showFeedback('success', 'Invite link deleted.')
    } catch {
      setLinksError('Unable to delete that link.')
    } finally {
      setBusy('')
    }
  }

  const copyInviteLinkHandler = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      showFeedback('success', 'Invite link copied.')
    } catch {
      showFeedback('error', 'Unable to copy the invite link.')
    }
  }

  const roleChangeHandler = async (collaboratorId, role) => {
    setBusy('update-role')
    setCollaboratorsError(null)
    try {
      const updated = await api.patchCollaboratorRole(code, access, collaboratorId, role)
      setCollaborators(current => current.map(c => (c.id === collaboratorId ? updated : c)))
      showFeedback('success', 'Role updated.')
    } catch {
      setCollaboratorsError('Unable to update that role.')
    } finally {
      setBusy('')
    }
  }

  const removeCollaboratorHandler = async (collaboratorId) => {
    setBusy('remove-collaborator')
    setCollaboratorsError(null)
    try {
      await api.deleteCollaborator(code, access, collaboratorId)
      setCollaborators(current => current.filter(c => c.id !== collaboratorId))
      showFeedback('success', 'Access removed.')
    } catch {
      setCollaboratorsError('Unable to remove that person.')
    } finally {
      setBusy('')
    }
  }

  const removeAllCollaboratorsHandler = async () => {
    setBusy('remove-all-collaborators')
    setCollaboratorsError(null)
    try {
      await api.deleteAllCollaborators(code, access)
      setCollaborators([])
      showFeedback('success', 'Everyone\'s access was removed.')
    } catch {
      setCollaboratorsError('Unable to remove everyone right now.')
    } finally {
      setBusy('')
    }
  }

  const closeHandler = () => {
    setOpen(false)
    clearFeedbackTimer()
    setFeedback(null)
  }

  useEffect(() => {
    if (!open) return undefined
    const escHandler = (event) => { if (event.key === 'Escape') closeHandler() }
    document.addEventListener('keydown', escHandler)
    return () => document.removeEventListener('keydown', escHandler)
  }, [open])

  const disabled = Boolean(busy) || !sharing
  const actionButton = (action, label, icon, className) => (
    <button
      type='button'
      className={`dashboard-sharing-button ${className}`}
      disabled={disabled}
      onClick={() => perform(action)}
    >
      {busy === action ? (
        <span className='dashboard-sharing-spinner' role='status' aria-label={`${label} in progress`} />
      ) : (
        <i className={`fa-solid ${icon}`} aria-hidden='true' />
      )}
      <span>{label}</span>
    </button>
  )

  return (
    <section className='dashboard-sharing-controls' aria-label='Dashboard sharing'>
      <button
        type='button'
        className='dashboard-sharing-button dashboard-sharing-button-primary'
        onClick={() => setOpen(true)}
      >
        <i className='fa-solid fa-share-nodes' aria-hidden='true' />
        <span>Share</span>
      </button>

      {open && (
        <div className='modal-container dashboard-sharing-modal-container' onClick={closeHandler}>
          <div
            className='modal-window dashboard-sharing-modal-window'
            role='dialog'
            aria-modal='true'
            aria-label='Dashboard sharing'
            onClick={e => e.stopPropagation()}
          >
            <div className='dashboard-sharing-modal-header'>
              <h2 className='dashboard-sharing-modal-title'>Share this dashboard</h2>
              <button type='button' className='dashboard-sharing-close-button' onClick={closeHandler} aria-label='Close sharing panel'>
                <i className='fa-solid fa-xmark' aria-hidden='true' />
              </button>
            </div>

            {busy === 'load' ? (
              <div className='dashboard-sharing-loader' role='status' aria-label='Loading sharing settings'>
                <span className='dashboard-sharing-spinner' aria-hidden='true' />
              </div>
            ) : (
              <div className='dashboard-sharing-modal-body'>
                <DashboardInviteLinksPanel
                  links={links}
                  busy={['create-link', 'revoke-link', 'delete-link'].includes(busy) ? busy : ''}
                  error={linksError}
                  justCreatedId={justCreatedId}
                  onCreate={createLinkHandler}
                  onRevoke={revokeLinkHandler}
                  onDelete={deleteLinkHandler}
                  onCopy={copyInviteLinkHandler}
                />

                <DashboardCollaboratorsPanel
                  collaborators={collaborators}
                  busy={['update-role', 'remove-collaborator', 'remove-all-collaborators'].includes(busy) ? busy : ''}
                  error={collaboratorsError}
                  onRoleChange={roleChangeHandler}
                  onRemove={removeCollaboratorHandler}
                  onRemoveAll={removeAllCollaboratorsHandler}
                />

                <div className='dashboard-sharing-section' aria-label='Public link'>
                  <h3 className='dashboard-sharing-section-title'>Public link</h3>
                  <p className='dashboard-sharing-section-hint'>
                    Anyone with this link can view a read-only copy of the dashboard, without signing in. This is
                    separate from the invite links above, which grant real accounts real edit or view access.
                  </p>
                  {!sharing ? null : sharing.enabled ? (
                    <div className='dashboard-sharing-actions'>
                      {actionButton('copy', 'Copy public link', 'fa-copy', 'dashboard-sharing-button-secondary')}
                      {actionButton('regenerate', 'Regenerate link', 'fa-rotate', 'dashboard-sharing-button-secondary')}
                      {actionButton('disable', 'Disable sharing', 'fa-link-slash', 'dashboard-sharing-button-danger')}
                    </div>
                  ) : (
                    <div className='dashboard-sharing-actions'>
                      {actionButton('enable', 'Enable sharing', 'fa-share-nodes', 'dashboard-sharing-button-primary')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {feedback && (
              <div className={`dashboard-sharing-toast dashboard-sharing-toast-${feedback.kind}`} role={feedback.kind === 'error' ? 'alert' : 'status'} aria-live={feedback.kind === 'error' ? 'assertive' : 'polite'}>
                <i className={`fa-solid ${feedback.kind === 'error' ? 'fa-circle-exclamation' : 'fa-circle-check'}`} aria-hidden='true' />
                <span>{feedback.message}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

export default DashboardSharingControls
